import axios, { AxiosError, AxiosResponse } from "axios";
import { Observable, Observer } from "rxjs";
import { singleton } from "tsyringe";
import * as uuid from "uuid";
import { Log } from "src/libs/log";
import { Token } from "src/containers/authentication/models/token";
import { DI } from "src/infra/di/container";
import {
    CancelableRequestProps,
    MicroServiceHeaders,
    RequestOptions,
} from "src/infra/http/types";
import { CookieStorageService } from "src/shared/services/cookie-storage/cookie-storage-service";
import { Environment } from "src/libs/environment";
import { BadRequestError } from "src/infra/http/errors/bad-request";
import { UnauthorizedError } from "src/infra/http/errors/unauthorized";
import { ForbiddenError } from "src/infra/http/errors/forbidden";
import { InternalServerError } from "src/infra/http/errors/internal-server";
import { NotFoundError } from "src/infra/http/errors/not-found";
import { GenericNetworkError } from "src/infra/http/errors/generic";

function unauthorized(status = 200): boolean {
    return status === 401 /* || status === 403 */;
}

/**
 * When the server answer back with an 401 or 403,
 * We understand that the logger should be logouted.
 */
axios.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error: AxiosError) => {
        if (unauthorized(error.response?.status)) {
            Log.warn("Error to fetch. Status code: ", error.response?.status);
            window.postMessage("unauthorized", "*");
        }
        return Promise.reject(error);
    }
);

@singleton()
export class HttpUtils {
    getUrl(path: string, host: string): string {
        return `${host}/${path}`;
    }

    getRealHeaders(
        customHeader: Record<string, any> | HeadersInit | null,
        isPublicRequest: boolean
    ): Record<string, any> | HeadersInit | undefined {
        if (customHeader) {
            return customHeader;
        }
        return isPublicRequest ? {} : this.getPrivateHeader();
    }

    getBaseHeaders() {
        return this.getPrivateHeader();
    }

    getMsBaseHeaders(
        userIdentity = window.userIdentity,
        businessProcess = "negocio",
        ocpKey = Environment.string("UX_APIM_OCP_KEY")
    ): MicroServiceHeaders {
        return {
            "Ocp-Apim-Subscription-Key": ocpKey,
            "Application-Name": "MEP",
            "User-Identity": userIdentity,
            "Application-User": userIdentity,
            "Id-Transaction": uuid.v4(),
            "Business-Process": businessProcess,
        };
    }

    getPrivateHeader(): object {
        // can't injection SessionService because circular dependencies
        const cookieService = DI.get(CookieStorageService);
        const token = cookieService.getCookie<object>("auth-token-client");
        const requestHeader: Record<string, any> = {
            "Content-Type": "application/json",
            Accept: "application/json",
        };
        if (token) {
            const { value: bearer } = Token.fromJson<Token>(token);
            requestHeader.Authorization = `bearer ${bearer}`;
        }
        return requestHeader;
    }

    getStreamEventHeader(): object {
        return {
            Accept: "text/event-stream",
        };
    }

    createFetchCancellableRequest<T>(
        params: CancelableRequestProps
    ): Observable<T> {
        const { url, method, headers, body, isFormData } = params;
        const controller = new AbortController();

        return new Observable((observer: Observer<T>) => {
            const request: Promise<Response> = fetch(url, {
                method: method.toLowerCase(),
                headers: headers as HeadersInit,
                body: isFormData
                    ? (body as BodyInit)
                    : body
                    ? JSON.stringify(body as BodyInit)
                    : null,
                signal: controller.signal,
            });

            request.then(
                (response) => {
                    if (response.ok) {
                        response
                            .json()
                            .then((data) => {
                                observer.next(data);
                                observer.complete();
                            })
                            .catch(() => {
                                observer.next({
                                    code: 200,
                                    status: "ok",
                                } as never);
                                observer.complete();
                            });
                    } else if (response.status === 400) {
                        observer.error(new BadRequestError());
                    } else if (response.status === 401) {
                        observer.error(new UnauthorizedError());
                    } else if (response.status === 403) {
                        observer.error(new ForbiddenError());
                    } else if (response.status === 404) {
                        observer.error(new NotFoundError());
                    } else if (response.status === 500) {
                        observer.error(new InternalServerError());
                    } else {
                        observer.error(new GenericNetworkError());
                    }
                },
                (err) => {
                    if (controller.signal.aborted) {
                        observer.complete();
                    } else {
                        observer.error(err);
                    }
                }
            );
        });
    }

    createCancellableRequest<T>(params: CancelableRequestProps): Observable<T> {
        const { url, method, headers, body } = params;
        return new Observable((observer: Observer<T>) => {
            const cancelToken = axios.CancelToken.source();
            let request: Promise<AxiosResponse> | null = null;

            switch (method.toUpperCase()) {
                case "GET":
                    request = axios.get(url, {
                        headers: headers,
                        cancelToken: cancelToken.token,
                    });
                    break;
                case "POST": {
                    request = axios.post(url, body, {
                        headers: headers,
                        cancelToken: cancelToken.token,
                    });
                    break;
                }
                case "PUT": {
                    request = axios.put(url, body, {
                        headers: headers,
                        cancelToken: cancelToken.token,
                    });
                    break;
                }
                case "PATCH": {
                    request = axios.patch(url, body, {
                        headers: headers,
                        cancelToken: cancelToken.token,
                    });
                    break;
                }
                case "DELETE": {
                    request = axios.delete(url, {
                        headers: headers,
                        cancelToken: cancelToken.token,
                    });
                    break;
                }
            }
            request?.then(
                (result) => {
                    observer.next(result.data ?? []);
                    observer.complete();
                },
                (err) => {
                    if (axios.isCancel(err)) {
                        observer.complete();
                    } else {
                        observer.error(err);
                    }
                }
            );

            return () => cancelToken.cancel();
        });
    }

    getRequestBasics(url: string, options: RequestOptions) {
        const {
            params = {},
            isPublicRequest = true,
            host = Environment.string("SERVER_MANAGEMENT_URL"),
            customHeader = null,
        } = options;
        const hasParams = Object.keys(params).length > 0;
        const queryParams = new URLSearchParams(params as Record<string, any>);
        const endpoint = hasParams
            ? this.getUrl(`${url}?${queryParams}`, host)
            : this.getUrl(url, host);
        const headers = this.getRealHeaders(customHeader, isPublicRequest);

        return { url: endpoint, headers };
    }
}

export const httpUtils = new HttpUtils();
